import { Component, computed, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ObraGlobalService } from '../../../core/navegacion/obra-global.service';
import { RAIZ_MENU, botonPorId, departamentoPorId, esPortadaDeMenu, migas, rutaDe, tarjetaDeRuta } from '../../../core/navegacion/registro';
import { VolverService, nombreDeDestino } from '../../../core/services/volver.service';
import { SelectorObra } from '../../../shared/components/selector-obra/selector-obra';

/**
 * La barra secundaria: pegada bajo la barra superior en TODAS las pantallas menos el inicio. A la izquierda el «Atrás» y la ruta de migas
 * (sale del REGISTRO de módulos, no de la dirección: «Inicio › Siscon › Digitar › Bodega»); a la derecha el selector de obra, solo donde
 * el módulo trabaja sobre una obra. En teléfono se compacta: «‹ Atrás» y el título, y el selector pasa a una segunda fila a todo el ancho.
 *
 * El «Atrás» lo decide VolverService (historial real; sin historial, el nivel anterior del registro; una pantalla de detalle puede declarar
 * su padre). Cambiar de obra no suma pasos al historial.
 */
@Component({
  selector: 'app-subbar',
  standalone: true,
  imports: [RouterLink, SelectorObra],
  templateUrl: './subbar.html',
  styleUrl: './subbar.scss',
})
export class Subbar {
  private volver = inject(VolverService);
  private g = inject(ObraGlobalService);

  private readonly url = this.g.url;

  readonly visible = computed(() => !esPortadaDeMenu(this.url()));
  readonly migas = computed(() => migas(this.url()));
  readonly muestraSelector = computed(() => this.g.modo() !== 'oculto');

  /** El título de la pantalla (el último tramo de la ruta) y su ícono. */
  readonly titulo = computed(() => this.migas().at(-1)?.etiqueta ?? '');
  readonly icono = computed(() => {
    const ruta = rutaDe(this.url());
    if (ruta === '/sin-acceso') return 'pi-lock';
    if (ruta.startsWith(RAIZ_MENU + '/')) {
      const [, dId, bId] = ruta.split('/').filter(Boolean);
      const dep = departamentoPorId(dId);
      return botonPorId(dep, bId)?.icon ?? dep?.icon ?? 'pi-th-large';
    }
    return tarjetaDeRuta(this.url())?.icon ?? 'pi-file';
  });

  /** El padre que declara una pantalla de detalle, salvo que se haya llegado desde otro módulo: entonces «Atrás» vuelve a ese módulo. */
  private readonly padre = computed(() => (this.volver.origenDeOtroModulo() ? null : this.volver.padreDeclarado()));

  readonly hayAtras = computed(() => this.padre() !== null || this.volver.destino() !== null);
  readonly etiquetaAtras = computed(() => this.padre()?.etiqueta ?? this.volver.destino()?.etiqueta ?? 'Atrás');

  /** Lo que dice el botón: el nombre de la pantalla adonde vuelve («Asistencia diaria»); si no se sabe cuál es, «Atrás». */
  readonly textoAtras = computed(() => nombreDeDestino(this.etiquetaAtras()) ?? 'Atrás');

  /** El nombre accesible empieza por el texto visible («Atrás»): así quien dicta «clic en Atrás» lo encuentra, y el lector dice adónde lleva. */
  readonly nombreAccesibleAtras = computed(() => {
    const e = this.etiquetaAtras();
    return e === 'Atrás' ? e : `Atrás: ${e.charAt(0).toLowerCase()}${e.slice(1)}`;
  });

  atras(): void {
    const declarado = this.padre();
    if (declarado) declarado.ir();
    else this.volver.volver();
  }

  /** La ruta de migas sin su último tramo (la pantalla actual, que va aparte con su ícono). */
  readonly tramosAnteriores = computed(() => this.migas().slice(0, -1));
}
